import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, FieldValue, BRANCH_LABEL } from "../components/common";
import { LeadTable } from "./Leads";
import { Sel } from "../components/LeadForm";
import { RecordAccess } from "../components/RecordAccess";

const inp = "bg-muted/60 border h-9 px-2 text-sm w-full";
const NOOP = () => {};
const NO_SELECTION = [];

const Classification = ({ a, cfg, patch }) => {
  const { can, field } = useAuth();
  return (
    <div className="lg:col-span-4 ax-panel p-4 space-y-3" data-testid="account-fields">
      <div className="ax-label">Classification</div>
      <label className="text-xs block"><span className="ax-label">BP category (tier)</span>
        {can("sales", "update") ? <Sel testid="account-tier" value={a.tier} onChange={(v) => patch({ tier: v || null })} opts={cfg.tiers.map((t) => ({ v: t.code, t: t.label }))} /> : <div>{a.tier || "—"}</div>}</label>
      <label className="text-xs block"><span className="ax-label">Annual revenue potential (₹ Lakhs)</span>
        {field("account.revenue_potential") === "edit" ? <input data-testid="account-revenue" type="number" defaultValue={a.revenue_potential || ""} onBlur={(e) => patch({ revenue_potential: e.target.value ? Number(e.target.value) : null })} className={inp} />
          : <div className="py-1"><FieldValue row={a} f="revenue_potential" testid="account-revenue" /></div>}</label>
    </div>
  );
};

const ContactAdd = ({ id, onAdded }) => {
  const { field } = useAuth();
  const [c, setC] = useState({ name: "" });
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const add = () => api.post(`/accounts/${id}/contacts`, c).then(() => { toast.success("Contact added"); setC({ name: "" }); onAdded(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
      <input data-testid="contact-name" placeholder="Name" value={c.name} onChange={set("name")} className={inp} />
      <input data-testid="contact-designation" placeholder="Designation" value={c.designation || ""} onChange={set("designation")} className={inp} />
      {field("contact.phone") === "edit" && <input data-testid="contact-phone" placeholder="Phone" value={c.phone || ""} onChange={set("phone")} className={inp} />}
      {field("contact.email") === "edit" && <input data-testid="contact-email" placeholder="Email" value={c.email || ""} onChange={set("email")} className={inp} />}
      <button data-testid="contact-add" disabled={!c.name} onClick={add} className="bg-primary text-primary-foreground h-9 text-sm disabled:opacity-50">Add contact</button>
    </div>
  );
};

const Contacts = ({ id, contacts, onAdded }) => {
  const { can } = useAuth();
  return (
    <div className="lg:col-span-8 ax-panel p-4" data-testid="account-contacts">
      <div className="ax-label mb-3">Contacts ({contacts.length})</div>
      <table className="ax-table w-full no-select"><thead><tr><th>Name</th><th>Designation</th><th>Phone</th><th>Email</th></tr></thead>
        <tbody>{contacts.map((ct) => <tr key={ct.contact_id}><td>{ct.name || "—"}</td><td>{ct.designation || "—"}</td><td><FieldValue row={ct} f="phone" /></td><td><FieldValue row={ct} f="email" /></td></tr>)}</tbody></table>
      {can("sales", "update") && <ContactAdd id={id} onAdded={onAdded} />}
    </div>
  );
};

export default function AccountDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get(`/accounts/${id}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { load(); api.get("/sales/config").then((r) => setCfg(r.data)).catch((e) => setErr(errMsg(e))); }, [load]);
  if (err) return <div className="ax-panel p-8 text-sm" data-testid="account-not-found">{err}</div>;
  if (!d || !cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const a = d.account;
  const patch = (changes) => api.patch(`/accounts/${id}`, { changes }).then(() => { toast.success("Saved"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div>
      <PageHeader eyebrow={<span className="font-mono">{a.account_id}</span>} title={a.name} desc={`${a.type || "—"} · ${BRANCH_LABEL[a.branch] || a.branch} · Owner ${a.owner || "—"}${a.domain ? ` · ${a.domain}` : ""}`} />
      <RecordAccess entity="account" entityId={a.account_id} />
      <div className="grid lg:grid-cols-12 gap-4 mb-6">
        <Classification a={a} cfg={cfg} patch={patch} />
        <Contacts id={id} contacts={d.contacts} onAdded={load} />
      </div>
      <div className="ax-label mb-2">Leads & opportunities for this account (only those you can access)</div>
      <LeadTable items={d.leads} selected={NO_SELECTION} setSelected={NOOP} selectable={false} onChanged={load} />
    </div>
  );
}
